import 'flatpickr/dist/flatpickr.css';
import React, { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { useSelector } from 'react-redux';
import { IRootState } from '../../store';

interface IProps {
  datesSelected?: DateSelectArg; // To prefill dates from the calendar
  edit: boolean;
  event: IEvent;
  toggleModel: (value: boolean) => void;
  fetchEvents: () => void;
}

import { DateSelectArg } from '@fullcalendar/core';
import { useMaskito } from '@maskito/react';
import { ErrorMessage, Field, FieldProps, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import * as Yup from 'yup';
import { eventDB, IEvent } from '../../db';
import { ISimpleEvent } from '../../db/Clients/event';
import { createID } from '../../utils/common-utils';
import { currencyMask } from '../../utils/mask';
import { toast, useErrorAlerts } from '../Alerts';

const CreateSimpleEvent: React.FC<IProps> = ({ edit, event, datesSelected, toggleModel, fetchEvents }) => {
  const oops = useErrorAlerts();
  const { t } = useTranslation();
  const {
    user,
    club: { selectedClub },
  } = useSelector((state: IRootState) => state);
  const maskedInputRef = useMaskito({ options: currencyMask('$') });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('EVENT_NAME_REQUIRED')),
    dates: Yup.mixed().required(t('DATES_REQUIRED')).default([]),
    overview: Yup.string(),
    email: Yup.string().required(t('SUPPORT_EMAIL_REQUIRED')).default(''),
    fee: Yup.string().required(t('FEE_REQUIRED')).default(''),
    type: Yup.string().required(t('DISCIPLINE_REQUIRED')),
  });

  const defaultParams = {
    name: '',
    dates: '',
    overview: '',
    email: selectedClub.clubEmail || '',
    fee: '',
    type: '',
  };

  const saveEvent = async (values: ISimpleEvent) => {
    try {
      const id = createID(values.name, 6);
      const owner = user.user.metadata?.id;
      const latest = edit ? await eventDB().get((values as any)._id) : null;

      if (owner && selectedClub._id) {
        const docValues = edit
          ? {
            ...latest,
            ...values,
            _rev: latest!._rev,
          }
          : {
            ...values,
            _id: id,
            club: selectedClub?._id,
            owner,
            clubName: selectedClub?.clubName,
            groups: [],
            matches: [],
            specialCat: [],
            aggs: [],
            doc_type: 'event',
            priceItem: '',
            featParti: false,
            featScores: false,
            featMatches: false,
            ageAgg: false,
            genderAgg: false,
            classAgg: false,
            agree: true,
            gateway: [],
            hidden: false,
          };
        const currency = selectedClub?.currency || 'cad';
        if (!currency) {
          toast({ title: t('SET_CURRENCY_FIRST'), timer: 6000 });
        }

        const newEvent = await eventDB().put(docValues as any);
        if (newEvent.id) {
          // getEvets();
          fetchEvents();
          toast({ title: t('EVENT_SAVED_SUCCESSFULLY') });
        }
        toggleModel(false);
      } else {
        toast({ title: t('SELECT_CLUB_FIRST') });
      }

    } catch (error) {
      oops(error, 'create_edit_simple_event');
    }
  };

  return (
    <div className="p-5">
      <Formik
        initialValues={defaultParams}
        validationSchema={validationSchema}
        onSubmit={(values) => saveEvent(values)}
      >
        {({ touched, errors, values, setFieldValue, setFieldTouched, setTouched }) => {

          const setDates = (dates: Date[]) => {
            setFieldValue('dates', dates.map(each => each.toLocaleDateString("en-CA"))), setTouched({ ...touched, ['dates']: true });
          };

          useEffect(() => {
            if (datesSelected) {
              setFieldValue('dates', [datesSelected.startStr, datesSelected.endStr]);
            }
          }, [datesSelected]);

          useEffect(() => {
            if (edit && event) {
              setFieldValue('_id', event._id, true);
              setFieldValue('name', event.name, true);
              setFieldValue('dates', [event.dates[0], event.dates[1]], true);
              setFieldValue('overview', event.overview, true);
              setFieldValue('email', event.email, true);
              setFieldValue('fee', event.fee, true);
              setFieldValue('type', event.type, true);
            }
          }, [event, edit]);

          return <Form className="space-y-5">
            <div>
              <label htmlFor="name">{t("EVENT_NAME")}:</label>
              <Field
                id="name"
                type="text"
                name="name"
                className="form-input"
                placeholder={t("ENTER_EVENT_NAME")}
              />
              <ErrorMessage name="name" component="div" className="text-danger mt-2" />
            </div>

            <div>
              <label htmlFor="start">{t("FROM")} :</label>
              <Field id="dates" name="dates">
                {({ field }: FieldProps) => (
                  <Flatpickr
                    options={{
                      mode: 'range',
                      dateFormat: 'Y-m-d',
                      position: 'auto left',
                    }}
                    id="dates"
                    className="form-input"
                    value={field.value}
                    onChange={(dates) => setDates(dates)}
                  />
                )}
              </Field>
              <ErrorMessage name="end" component="div" className="text-danger mt-2" />
            </div>

            <div>
              <label htmlFor="overview">{t("EVENT_DESCRIPTION")} :</label>
              <Field id="overview" name="overview">
                {({ field }: any) => (
                  <ReactQuill theme="snow" value={field.value} onChange={(e) => setFieldValue(field.name, e)} onFocus={() => setFieldTouched(field.name, true)} />
                )}
              </Field>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 md:gap-4 space-y-4 md:space-y-0'>
              <div className={`${errors.email && touched.email ? 'has-error' : ''} col-span-2`}>
                <label htmlFor="emailLabel">{t("CONTACT_EMAIL")}</label>
                <fieldset aria-labelledby="emailLabel">
                  <label htmlFor="email" className="text-white-dark">
                    {t("EXAMPLE_EMAIL")}
                  </label>
                  <Field name="email" type="email" id="email" className="form-input" />
                </fieldset>
                {errors.email && touched.email && <div className="text-danger mt-1">{errors.email as any}</div>}
              </div>

              {/* Fee */}
              <div className={errors.fee && touched.fee ? 'has-error' : ''}>
                <label htmlFor="fee">{t("ENTRY_FEE")}</label>
                <fieldset>
                  <label htmlFor="fee" className="text-white-dark">
                    {t("EXAMPLE_FEE")}
                  </label>
                  <input id="fee" type="text" value={values.fee} onBlur={() => setFieldTouched('fee')} onInput={(e) => setFieldValue('fee', e.currentTarget.value)} ref={maskedInputRef} className="form-input" />
                </fieldset>
              </div>
            </div>

            <div>
              <label>{t("DISCIPLINES")}</label>
              <div className="mt-3">
                {selectedClub.clubDisciplines?.map((discipline, index) => (
                  <label key={index} className="inline-flex cursor-pointer mr-3">
                    <Field
                      type="radio"
                      className="form-radio"
                      name="type"
                      value={discipline.name}
                    />
                    <span className="pl-2">{discipline.name}</span>
                  </label>
                ))}
                <ErrorMessage name="type" component="div" className="text-danger mt-2" />
              </div>
            </div>

            {errors && Object.keys(errors).length > 0 && <div className="text-danger mt-2">{t("CHECK_FORM")}</div>}

            <div className="flex justify-end items-center !mt-8">
              <button type="button" className="btn btn-outline-danger" onClick={() => toggleModel(false)}>
                {t("CANCEL")}
              </button>
              <button type="submit" className="btn btn-primary ml-4">
                {edit ? t("UPDATE_EVENT") : t("CREATE_EVENT")}
              </button>
            </div>
          </Form>
        }}
      </Formik>
    </div >
  );
};

export default CreateSimpleEvent;
